<ng-container *transloco="let t">
  <z-loading-overlay [target]="'grid-' + gridId" [blocked]="loading$ | async" size="auto">
    <div class="loading-spinner">
      <svg viewBox="0 0 100 25">
        <g class="blue-dot-2">
          <circle class="zethcon-blue blue-dot-1" cx="30%" cy="50%" r="5" />
        </g>

        <g class="grey-dot-2">
          <circle class="zethcon-grey grey-dot-1" cx="50%" cy="50%" r="5" />
        </g>

        <g class="orange-dot-2">
          <circle class="zethcon-orange orange-dot-1" cx="70%" cy="50%" r="5" />
        </g>
      </svg>
    </div>
  </z-loading-overlay>

  <p-table
    #table
    [id]="gridId"
    [dataKey]="dataKey"
    [rowTrackBy]="rowTrackBy"
    [tableStyle]="{ minWidth: 'auto' }"
    (onColReorder)="colReorderHandler($event)"
    (onColResize)="colResizeHandler($event)"
    (onContextMenuSelect)="contextMenuSelectHandler($event)"
    (onFilter)="filterHandler($event)"
    (onLazyLoad)="lazyLoadHandler($event)"
    (onPage)="pageHandler($event)"
    (onRowSelect)="rowSelectHandler($event)"
    (onRowUnselect)="rowUnselectHandler($event)"
    (onSort)="sortHandler($event)"
    [lazy]="lazy"
    [lazyLoadOnInit]="lazyLoadOnInit"
    [first]="gridService.first$(gridId) | async"
    [rows]="gridService.rows$(gridId) | async"
    [totalRecords]="gridService.totalRows$(gridId) | async"
    [columns]="gridService.selectedColumns$(gridId) | async"
    [value]="gridService.data$(gridId) | async"
    [metaKeySelection]="true"
    [selectionMode]="selectionMode"
    [(selection)]="rowSelection"
    [rowHover]="true"
    [contextMenu]="gridContextMenu.contextMenu"
    [(contextMenuSelection)]="contextMenuSelection"
    contextMenuSelectionMode="joint"
    [paginator]="paginator"
    [showCurrentPageReport]="showCurrentPageReport"
    [showPageLinks]="showPageLinks"
    [pageLinks]="pageLinks$ | async"
    [editMode]="editMode"
    [rowsPerPageOptions]="rowsPerPageOptions$ | async"
    [showFirstLastIcon]="showFirstLastIcon$ | async"
    paginatorDropdownAppendTo="body"
    currentPageReportTemplate="{{
      t('core.components.grid.common.current_page_report_template', { first: '{first}', last: '{last}', totalRecords: '{totalRecords}' })
    }}"
    [styleClass]="'p-datatable-sm p-datatable-gridlines p-datatable-striped p-datatable-grid grid-' + gridId"
    sortMode="multiple"
    [multiSortMeta]="sorts"
    [showInitialSortBadge]="false"
    [filters]="filters"
    [reorderableColumns]="true"
    columnResizeMode="expand"
    [resizableColumns]="true"
    [scrollable]="true"
    [scrollHeight]="gridHeight$ | async"
  >
    <ng-template pTemplate="header" let-columns>
      <tr z-grid-table-header-cell [columns]="columns">
        <th
          *ngIf="enableRowEditing"
          class="edit-controls"
          [pTooltip]="'core.components.grid.edit_controls.column_header' | transloco"
          [escape]="false"
          tooltipPosition="bottom"
          [showDelay]="500"
        >
          <i class="fa fa-pencil"></i>
        </th>
      </tr>
    </ng-template>

    <ng-template
      pTemplate="body"
      let-data
      let-columns="columns"
      let-expanded="expanded"
      let-rowgroup="rowgroup"
      let-rowspan="rowspan"
      let-index="rowIndex"
      let-editing="editing"
    >
      <tr
        z-grid-table-body-cell
        #tableRow
        class="p-selectable-row"
        [gridId]="gridId"
        [class.currently-editing]="editing"
        [pSelectableRow]="data"
        [pSelectableRowIndex]="index"
        [pSelectableRowDblClick]="data"
        [pContextMenuRow]="data"
        [pContextMenuRowIndex]="index"
        [data]="data"
        [columns]="columns"
        [expanded]="expanded"
        [rowgroup]="rowgroup"
        [rowspan]="rowspan"
        [index]="index"
        [ngClass]="data?.meta?.attributes?.row?.class"
        [ngStyle]="data?.meta?.attributes?.row?.style"
        [editing]="editing"
        [enableRowEditing]="enableRowEditing"
        [rowEditingEnableConditionColumn]="rowEditingEnableConditionColumn"
        [rowEditingEnableConditionValue]="rowEditingEnableConditionValue"
        [pEditableRow]="data"
      >
        <ng-container
          *ngIf="
            enableRowEditing &&
              checkRowEditingCondition(
                rowEditingEnableConditionColumn,
                rowEditingEnableConditionValue,
                data[rowEditingEnableConditionColumn]
              );
            then controls;
            else no_controls
          "
        ></ng-container>

        <ng-template #controls>
          <td class="edit-controls">
            <p-button
              *ngIf="!editing"
              pInitEditableRow
              icon="fa fa-pencil"
              (click)="onRowEditInitHandler(data, index)"
              styleClass="p-button-text edit-init-button"
              [pTooltip]="'core.components.grid.edit_controls.button' | transloco"
              [escape]="false"
              tooltipPosition="bottom"
              [showDelay]="500"
            ></p-button>

            <p-button
              *ngIf="editing"
              icon="pi pi-check"
              (click)="onRowEditSaveHandler(data, index, tableRow.elementRef.nativeElement)"
              styleClass="p-button-text p-button-success"
              [pTooltip]="'core.components.grid.edit_controls.save_changes' | transloco"
              [escape]="false"
              tooltipPosition="bottom"
              [showDelay]="500"
            ></p-button>

            <p-button
              *ngIf="editing"
              pCancelEditableRow
              icon="pi pi-times"
              (click)="onRowEditCancelHandler(data, index)"
              styleClass="p-button-text p-button-danger"
              [pTooltip]="'core.components.grid.edit_controls.cancel' | transloco"
              [escape]="false"
              tooltipPosition="bottom"
              [showDelay]="500"
            ></p-button>
          </td>
        </ng-template>

        <ng-template #no_controls>
          <td class="edit-controls" *ngIf="enableRowEditing"></td>
        </ng-template>
      </tr>
    </ng-template>

    <ng-template pTemplate="footer" let-columns>
      <tr
        z-grid-table-footer-cell
        [columns]="columns"
        [class.hidden]="(gridService.summaryRow$(gridId) | async) === null"
        [gridId]="gridId"
      >
        <td class="edit-controls" *ngIf="enableRowEditing"></td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <ng-container *ngIf="(gridService.initialized$(gridId) | async) === true">
        <z-no-results [gridId]="gridId"></z-no-results>
      </ng-container>
    </ng-template>

    <ng-template pTemplate="paginatorfirstpagelinkicon">
      <i class="far fa-angles-left"></i>
    </ng-template>

    <ng-template pTemplate="paginatorlastpagelinkicon">
      <i class="far fa-angles-right"></i>
    </ng-template>

    <ng-template pTemplate="paginatorpreviouspagelinkicon">
      <i class="far fa-angle-left"></i>
    </ng-template>

    <ng-template pTemplate="paginatornextpagelinkicon">
      <i class="far fa-angle-right"></i>
    </ng-template>
  </p-table>

  <z-grid-context-menu #gridContextMenu [items]="contextMenuItems"></z-grid-context-menu>
</ng-container>
